/**
 * nwslData.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Data layer for the NWSL component.
 * Source: ESPN standings + top scorers, April 2026 (GW5 of 30).
 * 2026 season: 16 teams, 2 expansion sides (Boston Legacy, Denver Summit).
 * Defending champion: Gotham FC. Shield holder: Kansas City Current.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Set this after running `wrangler deploy` for the nwsl-proxy worker ────────
// Format: https://nwsl-proxy.footballgirl.workers.dev/nwsl
const PROXY_URL = 'https://nwsl-proxy.footballgirl.workers.dev/nwsl';

// ─── Season meta ─────────────────────────────────────────────────────────────
export const NWSL_SEASON     = '2026';
export const NWSL_CURRENT_GW = 5;
export const NWSL_TOTAL_GW   = 30;
export const NWSL_SCRAPED    = '2026-04-21';

// ─── Standings (GW5, ESPN + FootyStats, April 2026) ──────────────────────────
// NWSL format: 3pts win, 1pt draw, 0 loss. Top 8 make playoffs.
// No relegation — bottom teams just miss playoffs.
// zone: 'po' = playoff (top 8), '' = out of playoffs
export const NWSL_STANDINGS_FALLBACK = [
    { pos:1,  short:'SD',  name:'San Diego Wave FC',     color:'#00AEEF', p:5, w:3, d:2, l:0, gf:10, ga:4,  pts:11, form:['W','D','W','W','D'], zone:'po' },
    { pos:2,  short:'POR', name:'Portland Thorns FC',    color:'#4B2B7F', p:5, w:3, d:1, l:1, gf:9,  ga:5,  pts:10, form:['W','W','L','W','D'], zone:'po' },
    { pos:3,  short:'SEA', name:'Seattle Reign FC',      color:'#003057', p:5, w:3, d:0, l:2, gf:8,  ga:6,  pts:9,  form:['W','L','W','L','W'], zone:'po' },
    { pos:4,  short:'LA',  name:'Angel City FC',         color:'#FEB927', p:4, w:2, d:2, l:0, gf:10, ga:5,  pts:8,  form:['W','D','W','D','W'], zone:'po' },
    { pos:5,  short:'HOU', name:'Houston Dash',          color:'#F7971E', p:4, w:2, d:1, l:1, gf:7,  ga:4,  pts:7,  form:['W','L','W','D','W'], zone:'po' },
    { pos:6,  short:'ORL', name:'Orlando Pride',         color:'#5C2D91', p:5, w:2, d:0, l:3, gf:7,  ga:8,  pts:6,  form:['W','L','L','W','L'], zone:'po' },
    { pos:7,  short:'UTA', name:'Utah Royals',           color:'#CE1126', p:4, w:1, d:2, l:1, gf:5,  ga:5,  pts:5,  form:['D','W','L','D','W'], zone:'po' },
    { pos:8,  short:'WAS', name:'Washington Spirit',     color:'#1D1D1B', p:5, w:1, d:2, l:2, gf:5,  ga:7,  pts:5,  form:['D','L','W','D','L'], zone:'po' },
    { pos:9,  short:'DEN', name:'Denver Summit FC',      color:'#003087', p:5, w:1, d:2, l:2, gf:4,  ga:7,  pts:5,  form:['D','L','D','W','L'], zone:''   },
    { pos:10, short:'BAY', name:'Bay FC',                color:'#E3001B', p:4, w:1, d:1, l:2, gf:5,  ga:7,  pts:4,  form:['W','L','D','L','L'], zone:''   },
    { pos:11, short:'KC',  name:'Kansas City Current',   color:'#2D3F7E', p:5, w:1, d:1, l:3, gf:4,  ga:8,  pts:4,  form:['L','L','W','D','L'], zone:''   },
    { pos:12, short:'NC',  name:'North Carolina Courage',color:'#A6192E', p:4, w:1, d:1, l:2, gf:5,  ga:6,  pts:4,  form:['D','W','L','L','D'], zone:''   },
    { pos:13, short:'GFC', name:'Gotham FC',             color:'#101820', p:5, w:1, d:0, l:4, gf:3,  ga:9,  pts:3,  form:['L','L','W','L','L'], zone:''   },
    { pos:14, short:'CHI', name:'Chicago Stars FC',      color:'#C8102E', p:5, w:0, d:3, l:2, gf:4,  ga:7,  pts:3,  form:['D','L','D','D','L'], zone:''   },
    { pos:15, short:'LOU', name:'Racing Louisville FC',  color:'#5C1F7A', p:4, w:0, d:2, l:2, gf:4,  ga:6,  pts:2,  form:['D','L','D','L','L'], zone:''   },
    { pos:16, short:'BOS', name:'Boston Legacy FC',      color:'#003087', p:5, w:0, d:1, l:4, gf:3,  ga:9,  pts:1,  form:['L','L','D','L','L'], zone:''   },
];

// ─── Top Scorers (ESPN, April 2026) ──────────────────────────────────────────
export const NWSL_TOP_SCORERS_FALLBACK = [
    { rank:1, name:'Kiki Van Zanten',     club:'Houston Dash',       goals:4, apps:4,  initials:'KV' },
    { rank:2, name:'Olivia Moultrie',     club:'Portland Thorns',    goals:3, apps:5,  initials:'OM' },
    { rank:2, name:'Barbra Banda',        club:'Orlando Pride',      goals:3, apps:5,  initials:'BB' },
    { rank:2, name:'Melissa Kössler',     club:'Denver Summit',      goals:3, apps:5,  initials:'MK' },
    { rank:2, name:'Reilyn Turner',       club:'Portland Thorns',    goals:3, apps:5,  initials:'RT' },
    { rank:2, name:'Lia Godfrey',         club:'San Diego Wave',     goals:3, apps:5,  initials:'LG' },
    { rank:2, name:'Ashley Sanchez',      club:'North Carolina',     goals:3, apps:4,  initials:'AS' },
    { rank:2, name:'Sveindís Jónsdóttir', club:'Angel City FC',      goals:3, apps:4,  initials:'SJ' },
    { rank:9, name:'Haley McCutcheon',    club:'Orlando Pride',      goals:2, apps:5,  initials:'HM' },
    { rank:9, name:'Dudinha',             club:'San Diego Wave',     goals:2, apps:5,  initials:'DU' },
];

// ─── Recent fixtures ──────────────────────────────────────────────────────────
export const NWSL_FIXTURES_FALLBACK = [
    { date:'2026-04-05', home:'Portland Thorns',   homeColor:'#4B2B7F', away:'Seattle Reign',    awayColor:'#003057', homeGoals:2, awayGoals:1, status:'FT' },
    { date:'2026-04-05', home:'San Diego Wave',    homeColor:'#00AEEF', away:'Angel City',       awayColor:'#FEB927', homeGoals:1, awayGoals:1, status:'FT' },
    { date:'2026-04-06', home:'Kansas City',       homeColor:'#2D3F7E', away:'Houston Dash',     awayColor:'#F7971E', homeGoals:0, awayGoals:2, status:'FT' },
    { date:'2026-04-12', home:'Orlando Pride',     homeColor:'#5C2D91', away:'Washington Spirit',awayColor:'#1D1D1B', homeGoals:2, awayGoals:0, status:'FT' },
    { date:'2026-04-13', home:'Gotham FC',         homeColor:'#101820', away:'San Diego Wave',   awayColor:'#00AEEF', homeGoals:0, awayGoals:2, status:'FT' },
    { date:'2026-04-26', home:'San Diego Wave',    homeColor:'#00AEEF', away:'Portland Thorns',  awayColor:'#4B2B7F', homeGoals:null, awayGoals:null, status:'NS' },
    { date:'2026-04-26', home:'Seattle Reign',     homeColor:'#003057', away:'Kansas City',      awayColor:'#2D3F7E', homeGoals:null, awayGoals:null, status:'NS' },
    { date:'2026-04-27', home:'Houston Dash',      homeColor:'#F7971E', away:'Angel City',       awayColor:'#FEB927', homeGoals:null, awayGoals:null, status:'NS' },
    { date:'2026-05-02', home:'Washington Spirit', homeColor:'#1D1D1B', away:'Orlando Pride',    awayColor:'#5C2D91', homeGoals:null, awayGoals:null, status:'NS' },
    { date:'2026-05-03', home:'Portland Thorns',   homeColor:'#4B2B7F', away:'Bay FC',           awayColor:'#E3001B', homeGoals:null, awayGoals:null, status:'NS' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
export function nwslPts(t) { return t.pts ?? (t.w * 3 + t.d); }
export function nwslGD(t)  { return t.gf - t.ga; }

// ─── Live fetch ───────────────────────────────────────────────────────────────
export async function fetchLiveNWSLData() {
    if (!PROXY_URL || PROXY_URL.includes('footballgirl')) {
        console.info('[NWSL] PROXY_URL not set — using fallback data.');
        return null;
    }
    try {
        const controller = new AbortController();
        const timeout    = setTimeout(() => controller.abort(), 5000);
        const res = await fetch(PROXY_URL, {
            signal: controller.signal,
            headers: { Accept: 'application/json' },
        });
        clearTimeout(timeout);
        if (!res.ok) throw new Error(`Proxy returned ${res.status}`);
        const json = await res.json();
        if (!Array.isArray(json.standings) || json.standings.length === 0) {
            throw new Error('Proxy returned empty standings');
        }
        console.info(`[NWSL] Live data loaded (scraped: ${json.meta?.scraped ?? 'unknown'})`);
        return {
            standings:  json.standings,
            topScorers: json.topScorers ?? NWSL_TOP_SCORERS_FALLBACK,
            fixtures:   json.fixtures   ?? NWSL_FIXTURES_FALLBACK,
            meta:       json.meta,
        };
    } catch (err) {
        console.warn('[NWSL] Live fetch failed, using fallback snapshot:', err.message);
        return null;
    }
}
